import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, exhaustMap, switchMap, tap } from 'rxjs/operators';
import { forkJoin, of } from 'rxjs';

import * as P2PActions from './p2p.actions';
import { PeerToPeerManageService } from '../services/peer-to-peer-manage/peer-to-peer-manage.service';
import { ToastService } from 'app/toast/services/toast.service';
import { FundraiserListService } from '../services/fundraiser-list/fundraiser-list.service';


@Injectable()
export class CampaignP2PEffects {

  loadP2PCampaignList$ = createEffect(() => {
    return this.actions$.pipe( 
      ofType(P2PActions.loadP2PCampaignList),
      exhaustMap((action: any) => {
        return this.peerToPeerManageService.getP2PCampaignList().pipe(
          map(data => P2PActions.loadP2PCampaignListSuccess({ data })),
          catchError(error => of(P2PActions.loadP2PCampaignListFailure({ error }))))
      })
    );
  });

  selectP2PCampaign$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(P2PActions.selectP2PCampaign),
      exhaustMap((action: any) => {
        const campaignId = action.payload;
        return this.peerToPeerManageService.getCampaignDetail(campaignId).pipe(
          map(data => P2PActions.selectP2PCampaignSuccess({ data })),
          catchError(error => of(P2PActions.selectP2PCampaignFailure({ error }))))
      })
    );
  });
  
  peerToPeerReport$ = createEffect(()=> {
    return this.actions$.pipe(
      ofType(P2PActions.peerToPeerReport),
      exhaustMap((action: any) => {
        return this.peerToPeerManageService.getPeerToPeerReport(action.payload).pipe(
          map((data: any) => {
            this.fundraiserListService.list.set(data);
            return P2PActions.peerToPeerReportSuccess({ data });
          }),
          catchError(error => of(P2PActions.peerToPeerReportFailure({ error }))))
      })
    );
  });

  peerToPeerInfo$ = createEffect(()=> {
    return this.actions$.pipe(
      ofType(P2PActions.peerToPeerInfo),
      exhaustMap((action: any) => {
        return this.peerToPeerManageService.getPeerToPeerInfo(action.payload).pipe(
          map((data: any) => {
            return P2PActions.peerToPeerInfoSuccess({ data });
          }),
          catchError(error => of(P2PActions.peerToPeerInfoFailure({ error }))))
      })
    );
  });

  addP2PDonation$ = createEffect(()=> {
    return this.actions$.pipe(
      ofType(P2PActions.addP2PDonation),
      exhaustMap((action: any) => {
        const p2pId = action.payload.params.peerToPeerId;
        return this.peerToPeerManageService.addDonation(action.payload.params).pipe(
          tap(() => {
            this.toastService.show('success', 'Donation Added Successfully', {delay: 2000});
          }),
 
          switchMap(() => forkJoin({ 
            peerToPeerInfo: this.peerToPeerManageService.getPeerToPeerInfo(p2pId),
            campaignList: this.peerToPeerManageService.getP2PCampaignList()
          })),
          switchMap(({peerToPeerInfo, campaignList}) => [
            P2PActions.addP2PDonationSuccess({ data: action.payload.params }),
            P2PActions.peerToPeerInfoSuccess({ data: peerToPeerInfo }),
            P2PActions.loadP2PCampaignListSuccess({ data: campaignList })
          ]),
          catchError(error => of(P2PActions.addP2PDonationFailure({ error }))))
      })
    );
  });

  updateP2P$ = createEffect(()=> {
    return this.actions$.pipe(
      ofType(P2PActions.updateP2P),
      exhaustMap((action: any) => {
        return this.peerToPeerManageService.update(action.payload).pipe(
          tap(() => {
            this.toastService.show('success', 'Updated Successfully', {delay: 2000});
          }),
          map((data) => P2PActions.updateP2PSuccess({ data })),
          catchError(error => of(P2PActions.updateP2PFailure({ error }))))
      })
    );
  });

  deleteP2PItem$ = createEffect(()=> {
    return this.actions$.pipe(
      ofType(P2PActions.deleteP2PItem),
      exhaustMap((action: any) => {
        return this.peerToPeerManageService.deleteP2P(action.payload).pipe(
          tap(() => {
            this.toastService.show('success', 'Deleted Successfully', {delay: 2000});
          }),
          switchMap(() => this.peerToPeerManageService.getP2PCampaignList()),
          switchMap((campaignList) => [
            P2PActions.deleteP2PItemSuccess({ data: action.payload }),
            P2PActions.loadP2PCampaignListSuccess({ data: campaignList })
          ]),
          catchError(error => of(P2PActions.deleteP2PItemFailure({ error }))))
      })
    );
  });

  deleteP2PPledge$ = createEffect(()=> {
    return this.actions$.pipe(
      ofType(P2PActions.deleteP2PPledge),
      exhaustMap((action: any) => {
        const p2pId = action.payload.p2pId;
        return this.peerToPeerManageService.deleteP2PPledge(action.payload.pledgeId).pipe(
          tap(() => {
            this.toastService.show('success', 'Pledge Deleted Successfully', {delay: 2000});
          }),
 
          switchMap(() => this.peerToPeerManageService.getPeerToPeerInfo(p2pId)),
          switchMap((peerToPeerInfo) => [
            P2PActions.deleteP2PPledgeSuccess({ data: action.payload }),
            P2PActions.peerToPeerInfoSuccess({ data: peerToPeerInfo }),
          ]),
          catchError(error => of(P2PActions.deleteP2PPledgeFailure({ error }))))
      })
    );
  });

  saveP2PEventCount$ = createEffect(()=> {
    return this.actions$.pipe(
      ofType(P2PActions.saveP2PEventCount),
      exhaustMap((action: any) => {

        return this.peerToPeerManageService.saveP2PEventCount(action.payload).pipe(
          tap(() => {
            this.toastService.show('success', 'Saved Successfully', {delay: 2000});
          }),
          map((data) => P2PActions.saveP2PEventCountSuccess({ data: action.payload })),
          catchError(error => of(P2PActions.saveP2PEventCountFailure({ error }))))
      })
    );
  });

  constructor(
    private actions$: Actions,
    private toastService: ToastService,
    private peerToPeerManageService: PeerToPeerManageService,
    private fundraiserListService: FundraiserListService
  ) {}

}
